import { useState, useEffect } from 'react';
import { fetchEndpoint } from '../../lib/api-client';

interface SectionData {
  data: unknown;
  loading: boolean;
  error: string | null;
  endpoint: string;
  responseTime: number;
}

function useFetchSection(path: string): SectionData {
  const [state, setState] = useState<SectionData>({
    data: null,
    loading: true,
    error: null,
    endpoint: path,
    responseTime: 0,
  });

  useEffect(() => {
    let cancelled = false;
    fetchEndpoint(path)
      .then((result) => {
        if (!cancelled) {
          setState({
            data: result.data,
            loading: false,
            error: null,
            endpoint: path,
            responseTime: result.responseTime,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to fetch',
          }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  return state;
}

function EndpointBadge({ path }: { path: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ti-cyan/10 text-ti-cyan text-xs font-mono">
      <span className="text-ti-green font-semibold">GET</span>
      <span>{path}</span>
    </span>
  );
}

function SkeletonBlock({ lines = 3 }: { lines?: number }) {
  return (
    <div className="animate-pulse space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="h-4 bg-ti-border rounded"
          style={{ width: `${70 + Math.random() * 30}%` }}
        />
      ))}
    </div>
  );
}

function SectionCard({
  title,
  endpoint,
  loading,
  error,
  children,
}: {
  title: string;
  endpoint: string;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-ti-surface border border-ti-border rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-ti-text">{title}</h3>
        <EndpointBadge path={endpoint} />
      </div>
      {loading && <SkeletonBlock lines={4} />}
      {error && <p className="text-ti-red text-sm">{error}</p>}
      {!loading && !error && children}
    </div>
  );
}

function SummarySection({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-2">
      <h2 className="text-2xl font-bold text-ti-text">
        {String(data.name ?? 'Ted Lango')}
      </h2>
      {data.title ? (
        <p className="text-ti-cyan font-medium">{String(data.title)}</p>
      ) : null}
      {data.thesis ? (
        <p className="text-ti-text-muted leading-relaxed">{String(data.thesis)}</p>
      ) : null}
      {data.headline ? (
        <p className="text-ti-text-muted leading-relaxed">{String(data.headline)}</p>
      ) : null}
      {data.location ? (
        <p className="text-sm text-ti-text-dim">
          {String(data.location)}
        </p>
      ) : null}
    </div>
  );
}

function TrackRecordSection({ data }: { data: unknown }) {
  const items = Array.isArray(data) ? data : [];

  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map(
        (item: { metric?: string; value?: string; context?: string }, i: number) => (
          <div
            key={i}
            className="bg-ti-bg border border-ti-border rounded-lg p-4 text-center"
          >
            <p className="text-xl font-bold text-ti-gold">
              {item.value || item.metric || '---'}
            </p>
            <p className="text-xs text-ti-text-muted mt-1">
              {item.context || item.metric || ''}
            </p>
          </div>
        )
      )}
    </div>
  );
}

function ExperienceSection({ data }: { data: unknown }) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const items = Array.isArray(data) ? data : [];

  return (
    <div className="space-y-3">
      {items.map(
        (
          role: {
            title?: string;
            company?: string;
            org?: string;
            period?: string;
            dates?: string;
            contributions?: string[];
            highlights?: string[];
            description?: string;
          },
          i: number
        ) => {
          const isOpen = expanded === i;
          const contributions =
            role.contributions || role.highlights || [];

          return (
            <div key={i} className="relative pl-6 border-l-2 border-ti-blue/30">
              {/* Timeline dot */}
              <div className="absolute left-[-5px] top-1 w-2 h-2 rounded-full bg-ti-blue" />

              <div className="bg-ti-bg border border-ti-border rounded-lg p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-ti-text">
                      {role.title || 'Role'}
                    </p>
                    <p className="text-sm text-ti-cyan">
                      {role.company || role.org || ''}
                    </p>
                  </div>
                  <span className="text-xs text-ti-text-dim whitespace-nowrap">
                    {role.period || role.dates || ''}
                  </span>
                </div>

                {role.description && (
                  <p className="text-sm text-ti-text-muted mt-2">
                    {role.description}
                  </p>
                )}

                {contributions.length > 0 && (
                  <>
                    <button
                      onClick={() => setExpanded(isOpen ? null : i)}
                      className="mt-2 text-xs text-ti-blue hover:text-ti-cyan transition-colors cursor-pointer"
                    >
                      {isOpen
                        ? 'Hide contributions'
                        : `Show ${contributions.length} contributions`}
                    </button>
                    {isOpen && (
                      <ul className="mt-2 space-y-1">
                        {contributions.map((c: string, j: number) => (
                          <li
                            key={j}
                            className="text-sm text-ti-text-muted flex items-start gap-2"
                          >
                            <span className="text-ti-green mt-1 shrink-0">
                              &bull;
                            </span>
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        }
      )}
    </div>
  );
}

function AccomplishmentsSection({ data }: { data: unknown }) {
  const items = Array.isArray(data) ? data : [];

  // Group by category if possible
  const grouped: Record<string, Array<{ description?: string; value?: string; impact?: string }>> = {};
  for (const item of items) {
    const cat = (item as Record<string, unknown>).category as string || 'General';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item as { description?: string; value?: string; impact?: string });
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([category, achievements]) => (
        <div key={category}>
          <h4 className="text-sm font-semibold text-ti-text-dim uppercase tracking-wider mb-2">
            {category}
          </h4>
          <div className="space-y-2">
            {achievements.map((a, i) => (
              <div
                key={i}
                className="bg-ti-bg border border-ti-border rounded-lg p-3 flex items-start gap-3"
              >
                <div className="shrink-0 w-1 h-full min-h-[2rem] rounded-full bg-ti-gold" />
                <div>
                  <p className="text-sm text-ti-text">
                    {a.description || JSON.stringify(a)}
                  </p>
                  {(a.value || a.impact) && (
                    <p className="text-xs text-ti-gold mt-1 font-medium">
                      {a.value || a.impact}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SkillsSection({ data }: { data: unknown }) {
  // Handle both array of skill objects and plain arrays
  const items = Array.isArray(data) ? data : [];

  // Check if skills are grouped objects with category+skills
  const hasCategories = items.length > 0 && typeof items[0] === 'object' && items[0] !== null &&
    ('category' in items[0] || 'group' in items[0]);

  if (hasCategories) {
    return (
      <div className="space-y-3">
        {items.map((group: Record<string, unknown>, i: number) => {
          const category = (group.category || group.group || 'General') as string;
          const skills = (group.skills || group.items || []) as string[];
          return (
            <div key={i}>
              <h4 className="text-xs font-semibold text-ti-text-dim uppercase tracking-wider mb-1.5">
                {category}
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {skills.map((skill: string, j: number) => (
                  <span
                    key={j}
                    className="px-2 py-0.5 bg-ti-purple/10 text-ti-purple text-xs rounded-full border border-ti-purple/20"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Flat array of strings
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((skill: unknown, i: number) => (
        <span
          key={i}
          className="px-2 py-0.5 bg-ti-purple/10 text-ti-purple text-xs rounded-full border border-ti-purple/20"
        >
          {typeof skill === 'string' ? skill : JSON.stringify(skill)}
        </span>
      ))}
    </div>
  );
}

export default function LiveAPIResume() {
  const summary = useFetchSection('/summary');
  const experience = useFetchSection('/experience');
  const accomplishments = useFetchSection('/accomplishments');
  const skills = useFetchSection('/skills');
  const trackRecord = useFetchSection('/track-record');

  const allLoading =
    summary.loading &&
    experience.loading &&
    accomplishments.loading &&
    skills.loading &&
    trackRecord.loading;

  return (
    <div className="space-y-4">
      {/* LIVE indicator */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-ti-green/5 border border-ti-green/20 rounded-full w-fit">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ti-green opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-ti-green" />
        </span>
        <span className="text-xs text-ti-green font-medium">
          {allLoading ? 'Fetching from Living Resume API...' : 'Fetched live from Living Resume API'}
        </span>
      </div>

      {/* Summary */}
      <SectionCard
        title="Summary"
        endpoint="/summary"
        loading={summary.loading}
        error={summary.error}
      >
        <SummarySection data={summary.data as Record<string, unknown>} />
      </SectionCard>

      {/* Track Record */}
      <SectionCard
        title="Track Record"
        endpoint="/track-record"
        loading={trackRecord.loading}
        error={trackRecord.error}
      >
        <TrackRecordSection data={trackRecord.data} />
      </SectionCard>

      {/* Experience */}
      <SectionCard
        title="Experience"
        endpoint="/experience"
        loading={experience.loading}
        error={experience.error}
      >
        <ExperienceSection data={experience.data} />
      </SectionCard>

      {/* Accomplishments */}
      <SectionCard
        title="Accomplishments"
        endpoint="/accomplishments"
        loading={accomplishments.loading}
        error={accomplishments.error}
      >
        <AccomplishmentsSection data={accomplishments.data} />
      </SectionCard>

      {/* Skills */}
      <SectionCard
        title="Skills"
        endpoint="/skills"
        loading={skills.loading}
        error={skills.error}
      >
        <SkillsSection data={skills.data} />
      </SectionCard>
    </div>
  );
}
